import { useAgentSelector, useAgentStepData } from "@/modules/agent/states";
import { ConfidenceTypes } from "@/modules/agent/types";
import { VendorRiskSectionEnum } from "@/modules/agent/types/vendorAssessment";
import {
  VendorSupplyChainInfrastructureItem,
  VendorSupplyChainSoftwareItem,
} from "@/modules/agent/types/vendorSupplyChain";
import { useAgentSocket } from "@/modules/agent/utils/agent-socket";
import { AgentSessionStepType } from "@/modules/sessions/types";
import { ZButton } from "@/shared/components/button";
import { ZTextarea } from "@/shared/components/input";
import {
  ZModal,
  ZModalBody,
  ZModalContent,
  ZModalFooter,
  ZModalHeader,
} from "@/shared/components/modal";
import { addNotification } from "@/shared/states/notification";
import { Input, Select, SelectItem } from "@nextui-org/react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { IdParams } from "../Overview/Overview";
import { useVendorRiskAssessmentStore } from "../useVendorRiskAssessment/useVendorRiskAssessmentStore";

export type IAddSubServicesPopupProps =
  | {
    handleClose: () => void;
    type: "EDIT";
    editKey: string;
    editIdx: number;
  }
  | {
    handleClose: () => void;
    type: "ADD";
  };

const AddSubServicesPopup = (props: IAddSubServicesPopupProps) => {
  const { id } = useParams<keyof IdParams>() as IdParams;
  const { handleClose } = props;
  const [selectedDropdownOption, setSelectedDropdownOption] = useState(props.type === "EDIT" ? props.editKey : "");
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const { setVendorSupplyChain, vendorSupplyChainData } = useVendorRiskAssessmentStore.getState();
  const { setSelectedId } = useAgentSelector.useActions();
  const stepData = useAgentStepData(id);
  const { emitControlEdit } = useAgentSocket();
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (props.type === "EDIT") {
      try {
        if (!vendorSupplyChainData || !Array.isArray(vendorSupplyChainData) || vendorSupplyChainData.length === 0) {
          return;
        }

        setSelectedDropdownOption(props.editKey);

        const category = vendorSupplyChainData.find(d => d.key === props.editKey);

        if (!category) {
          return;
        }

        if (!category.value || !category.value.value || !Array.isArray(category.value.value)) {
          return;
        }

        if (props.editIdx < 0 || props.editIdx >= category.value.value.length) {
          return;
        }

        const item = category.value.value[props.editIdx];

        if (!item) {
          return;
        }

        if (props.editKey === 'infrastructure') {
          // Safe to cast since we've checked the key matches the infrastructure type
          const infraItem = item as VendorSupplyChainInfrastructureItem;
          setName(infraItem.infrastructure_provider || "");
        } else if (props.editKey === 'software') {
          // Safe to cast since we've checked the key matches the software type
          const softwareItem = item as VendorSupplyChainSoftwareItem;
          setName(softwareItem.software || "");
        } else {
          // Fallback using type checking with 'in' operator
          const possibleName = 'infrastructure_provider' in item
            ? item.infrastructure_provider
            : 'software' in item
              ? item.software
              : '';
          setName(possibleName);
        }

        setPurpose(item.purpose || "");
        setDataLoaded(true);
      } catch (error) {
      // Keep dialog open for user to retry or discard
      }
    } else {
      setSelectedDropdownOption(selectedDropdownOption || "software");
      setDataLoaded(true);
    }
  }, [props, vendorSupplyChainData]);

  const buttonDisabled = useMemo(() => {
    return (
      selectedDropdownOption.length === 0 ||
      name.length === 0 ||
      purpose.length === 0
    )
  }, [name.length, purpose.length, selectedDropdownOption.length]);

  const getEditStepData = () => {
    return stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);
  };

  const emitSupplyChainUpdate = async (category: string, control: any) => {
    const editStepData = getEditStepData();
    if (editStepData?.data?.url) {
      try {
        await emitControlEdit(
          editStepData.data.url,
          {
            ...control,
            section: VendorRiskSectionEnum.vendor_supply_chain
          },
          category,
          id,
          editStepData.id,
          ""
        );
      } catch (error) {
        // Error handling is managed by the socket service
      }
    }
  };

  const handleSubmitClick = async () => {
    const temp = [...vendorSupplyChainData];

    if (props.type === "ADD") {
      const vendorSupplySelectedkey = temp.find(
        (d) => d.key === selectedDropdownOption,
      );

      if (vendorSupplySelectedkey) {
        const isDuplicate = vendorSupplySelectedkey.value.value.some(item => {
          const itemName = 'infrastructure_provider' in item
            ? item.infrastructure_provider
            : 'software' in item
              ? item.software
              : '';
          return itemName.toLowerCase().trim() === name.toLowerCase().trim() &&
            item.purpose.toLowerCase().trim() === purpose.toLowerCase().trim();
        });

        if (isDuplicate) {
          addNotification({
            type: "error",
            message: "A subservice already exists"
          });
          return;
        }

        if (vendorSupplySelectedkey.key === "infrastructure") {
          vendorSupplySelectedkey.value.value.push({
            purpose: purpose,
            infrastructure_provider: name,
            confidence: ConfidenceTypes.DNF,
            sources: [],
          } as VendorSupplyChainInfrastructureItem);
        }
        if (vendorSupplySelectedkey.key === "software") {
          vendorSupplySelectedkey.value.value.push({
            purpose: purpose,
            software: name,
            confidence: ConfidenceTypes.DNF,
            sources: [],
          } as VendorSupplyChainSoftwareItem);
        }

        setVendorSupplyChain(temp);
        setSelectedId(id, vendorSupplySelectedkey.key);

        await emitSupplyChainUpdate(selectedDropdownOption, vendorSupplySelectedkey);
      }
    } else {
      const vendorSupplySelectedkey = temp.find((d) => d.key === props.editKey);
      if (vendorSupplySelectedkey && vendorSupplySelectedkey.value &&
        vendorSupplySelectedkey.value.value &&
        Array.isArray(vendorSupplySelectedkey.value.value) &&
        props.editIdx < vendorSupplySelectedkey.value.value.length) {

        const oldKey = vendorSupplySelectedkey.key;

        if (oldKey === selectedDropdownOption) {
          // Same type, just update the item
          if (!vendorSupplySelectedkey.value.value[props.editIdx]) {
            return;
          }

          // Get the existing item
          const existingItem = vendorSupplySelectedkey.value.value[props.editIdx];
          // Get confidence and sources with safe type checking
          const confidence = existingItem.confidence || ConfidenceTypes.DNF;
          const sources = existingItem.sources || [];

          // Update item based on selected type
          if (selectedDropdownOption === "software") {
            vendorSupplySelectedkey.value.value[props.editIdx] = {
              purpose: purpose,
              software: name,
              confidence: confidence,
              sources: sources,
            } as VendorSupplyChainSoftwareItem;
          } else {
            vendorSupplySelectedkey.value.value[props.editIdx] = {
              purpose: purpose,
              infrastructure_provider: name,
              confidence: confidence,
              sources: sources,
            } as VendorSupplyChainInfrastructureItem;
          }

          setVendorSupplyChain(temp);
          setSelectedId(id, selectedDropdownOption);

          await emitSupplyChainUpdate(oldKey, vendorSupplySelectedkey);
        } else {
          // Type change - remove from one array and add to the other
          if (!vendorSupplySelectedkey.value.value[props.editIdx]) {
            return;
          }

          // Remove the item from the old array
          const oldItem = vendorSupplySelectedkey.value.value.splice(props.editIdx, 1)[0];

          if (!oldItem) {
            return;
          }

          setVendorSupplyChain([...temp]);
          await emitSupplyChainUpdate(oldKey, vendorSupplySelectedkey);

          // Now find the target category and add the item there
          const vendorSupplySelectedkeyNew = temp.find((d) => d.key === selectedDropdownOption);
          if (vendorSupplySelectedkeyNew && vendorSupplySelectedkeyNew.value &&
            vendorSupplySelectedkeyNew.value.value &&
            Array.isArray(vendorSupplySelectedkeyNew.value.value)) {
            // Add to the target array with the new structure
            let newItem;
            const confidence = oldItem.confidence || ConfidenceTypes.DNF;
            const sources = oldItem.sources || [];

            if (vendorSupplySelectedkeyNew.key === "infrastructure") {
              newItem = {
                purpose: purpose,
                infrastructure_provider: name,
                confidence: confidence,
                sources: sources,
              } as VendorSupplyChainInfrastructureItem;
              vendorSupplySelectedkeyNew.value.value.push(newItem);
            }

            if (vendorSupplySelectedkeyNew.key === "software") {
              newItem = {
                purpose: purpose,
                software: name,
                confidence: confidence,
                sources: sources,
              } as VendorSupplyChainSoftwareItem;
              vendorSupplySelectedkeyNew.value.value.push(newItem);
            }

            setVendorSupplyChain([...temp]);
            setSelectedId(id, selectedDropdownOption);

            await emitSupplyChainUpdate(selectedDropdownOption, vendorSupplySelectedkeyNew);
          }
        }
      }
    }

    addNotification({
      type: "success",
      message:
        props.type === "ADD"
          ? "New subservice added successfully"
          : "Subservice updated successfully",
    });

    handleClose();
  };

  return (
    <ZModal
      isOpen={true}
      onOpenChange={() => {
        handleClose();
      }}
      aria-label="Add Sub Services"
      className="max-h-[calc(100vh-150px)] overflow-y-auto"
    >
      <ZModalContent className="max-w-lg flex flex-col gap-6">
        <ZModalHeader className="flex text-lg flex-col w-full items-center pb-0">
          <p>{props.type === "ADD" ? "Add" : "Edit"} Subservice</p>
        </ZModalHeader>

        <ZModalBody className="pt-0 overflow-auto flex gap-8">
          {!dataLoaded && props.type === "EDIT" ? (
            <div className="w-full text-center py-4">Loading subservice data...</div>
          ) : (
            <>
                <Select
                  items={[
                    { key: "software", label: "Software" },
                    { key: "infrastructure", label: "Infrastructure" },
                  ]}
                  label={"Type"}
                  labelPlacement="outside"
                  value={selectedDropdownOption}
                  placeholder="Select Type"
                  onChange={(value) => {
                    setSelectedDropdownOption(value.target.value);
                  }}
                  selectedKeys={
                    selectedDropdownOption ? [selectedDropdownOption] : []
                  }
                >
                  {(item) => (
                    <SelectItem key={item.key} value={item.key}>
                      {item.label}
                    </SelectItem>
                  )}
                </Select>

                <Input
                  label="Name"
                  labelPlacement="outside"
                  placeholder="Add name"
                  value={name}
                  onChange={(e) => setName(e.target.value.trimStart())}
                  className="w-full shadow-none rounded-sm bg-white"
                />
                <ZTextarea
                  label="Purpose"
                  labelPlacement="outside"
                  placeholder="Add purpose"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value.trimStart())}
                  className="w-full rounded-sm bg-white"
                />
            </>
          )}
        </ZModalBody>
        <ZModalFooter className="flex gap-4">
          <ZButton
            variant="bordered"
            onClick={() => handleClose()}
            className="px-3 py-1 text-medium flex-1"
          >
            Discard
          </ZButton>
          <ZButton
            type="submit"
            onClick={handleSubmitClick}
            isDisabled={buttonDisabled || !dataLoaded}
            className="px-3 py-1 text-medium flex-1"
          >
            Submit
          </ZButton>
        </ZModalFooter>
      </ZModalContent>
    </ZModal>
  );
};

export default AddSubServicesPopup;